import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useEffect as useReactEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { CallProvider } from "@/context/CallContext";
import { BottomNav } from "@/components/BottomNav";
import OnboardingPage from "@/pages/OnboardingPage";
import Lineage from "@/pages/Lineage";
import Hangouts from "@/pages/Hangouts";
import Reunions from "@/pages/Reunions";
import Memories from "@/pages/Memories";
import Vault from "@/pages/Vault";
import MembersPage from "@/pages/MembersPage";
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import MessagesThread from "@/pages/MessagesThread";
import SupportThread from "@/pages/SupportThread";
import PublicProfile from "@/pages/PublicProfile";
import FamilyLanding from "@/pages/FamilyLanding";
import PromoLanding from "@/pages/PromoLanding";
import SuperAdmin from "@/pages/SuperAdmin";
import Marketing from "@/pages/Marketing";
import PlatformLogin from "@/pages/PlatformLogin";
import LegalPage from "@/pages/LegalPage";
import AuthScreen from "@/pages/AuthScreen";
import { DesktopSidebar } from "@/components/DesktopSidebar";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { PausedScreen } from "@/components/PausedScreen";
import { TenantChooser } from "@/components/TenantChooser";
import { initActivity, trackPage, endActivity } from "@/lib/activity";

// Drives client-side activity tracking: starts a session on login (never for super admin),
// records route changes, and ends the session on logout.
function ActivityTracker() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  useReactEffect(() => {
    if (user && !user.is_super_admin) initActivity(user);
    else endActivity();
  }, [user]);
  useReactEffect(() => {
    if (user && !user.is_super_admin) trackPage(pathname);
  }, [pathname, user]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useReactEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector(".app-shell")?.scrollTo?.(0, 0);
    document.querySelector(".fv-main")?.scrollTo?.(0, 0);
  }, [pathname]);
  return null;
}

function Loader() {
  return (
    <div className="fv-screen grain grid place-items-center">
      <div className="font-serif-display text-3xl gold-text animate-pulse">The Family Vault</div>
    </div>
  );
}

function Page({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      {children}
    </motion.div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/lineage" element={<Page><Lineage /></Page>} />
        <Route path="/hangouts" element={<Page><Hangouts /></Page>} />
        <Route path="/reunions" element={<Page><Reunions /></Page>} />
        <Route path="/memories" element={<Page><Memories /></Page>} />
        <Route path="/vault" element={<Page><Vault /></Page>} />
        <Route path="/members" element={<Page><MembersPage /></Page>} />
        <Route path="/profile" element={<Page><ProfilePage /></Page>} />
        <Route path="/settings" element={<Page><SettingsPage /></Page>} />
        <Route path="/payment/success" element={<Page><PaymentSuccess /></Page>} />
        <Route path="/payment/cancel" element={<Page><PaymentCancel /></Page>} />
        <Route path="/messages/:userId" element={<Page><MessagesThread /></Page>} />
        <Route path="/support" element={<Page><SupportThread /></Page>} />
        <Route path="/u/:id" element={<Page><PublicProfile /></Page>} />
        <Route path="/a/:id" element={<Page><PublicProfile ancestor /></Page>} />
        <Route path="*" element={<Navigate to="/lineage" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

// Authenticated family app: runs the tenant gates then renders the in-app routes.
function AppShell() {
  const { user, family } = useAuth();
  const { pathname } = useLocation();
  const [tenantConfirmed, setTenantConfirmed] = React.useState(() => !!sessionStorage.getItem("fv_tenant_confirmed"));
  const [railOpen, setRailOpen] = React.useState(false);

  // Collapsible sidebar (600–799px): toggle via header button, auto-close on navigate.
  React.useEffect(() => { setRailOpen(false); }, [pathname]);
  React.useEffect(() => {
    const t = () => setRailOpen((o) => !o);
    window.addEventListener("fv:toggle-rail", t);
    return () => window.removeEventListener("fv:toggle-rail", t);
  }, []);

  // Paused account -> support-only screen (checked before family load, since
  // /families/current returns 423 for paused members and would clear `family`)
  if (user.paused)
    return <div className="fv-screen grain"><PausedScreen /></div>;

  // Belongs to more than one family -> choose a tenant on login
  if (user.family_count > 1 && !tenantConfirmed)
    return <div className="fv-screen grain"><TenantChooser onChosen={() => setTenantConfirmed(true)} /></div>;

  if (!user.active_family_id || !family)
    return <OnboardingPage />;

  // Approved into a paid tier or owes dues -> payment gate (charged only after admission)
  const needsPlan = family.tier && family.tier !== "roots" && (user.sub_status === "pending_payment" || family.sub_status === "pending_payment");
  const needsDues = user.dues_pending && !user.dues_active;
  if (needsPlan || needsDues)
    return <div className="fv-screen grain"><SubscriptionGate needsPlan={needsPlan} needsDues={needsDues} /></div>;

  const contentClass =
    (pathname.startsWith("/lineage") || /^\/(hangouts|reunions|memories|vault)(\/|$)/.test(pathname)) ? "fv-content-full"
    : (pathname.startsWith("/members") || pathname.startsWith("/profile")) ? "fv-content-wide"
    : pathname.startsWith("/settings") ? "fv-content-form"
    : "fv-content";

  return (
    <div className={`fv-app grain${railOpen ? " rail-open" : ""}`}>
      <DesktopSidebar />
      <div className="fv-rail-backdrop" onClick={() => setRailOpen(false)} data-testid="rail-backdrop" />
      <div className="fv-main">
        <ScrollToTop />
        <CallProvider>
          <div className={contentClass} style={{ paddingBottom: 96 }}>
            <AnimatedRoutes />
          </div>
          <BottomNav />
        </CallProvider>
      </div>
    </div>
  );
}

// Root + all in-app paths. Logged-out visitors get the marketing site (or a
// family invite landing when arriving via an invite link).
function MainRoute() {
  const { user } = useAuth();
  const location = useLocation();

  if (user === null) return <Loader />;
  if (user && user.is_super_admin) return <Navigate to="/x1" replace />;

  if (user === false) {
    const params = new URLSearchParams(location.search);
    const qInvite = params.get("invite");
    if (qInvite) {
      const code = qInvite.toUpperCase();
      localStorage.setItem("fv_invite", code);
      return <FamilyLanding inviteCode={code} />;
    }
    if (location.pathname === "/") return <Marketing />;
    return <Navigate to="/" replace />;
  }

  return <AppShell />;
}

function LoginRoute() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (user && user.is_super_admin) return <Navigate to="/x1" replace />;
  if (user) return <Navigate to="/" replace />;
  const mode = new URLSearchParams(location.search).get("mode") === "register" ? "register" : "login";
  return <AuthScreen mode={mode} />;
}

// Hidden platform console entry.
function SuperAdminRoute() {
  const { user } = useAuth();
  // Give the admin route its own PWA identity so "Add to Home Screen" saves a
  // separate shortcut that launches directly into /x1 (iOS uses the manifest's start_url).
  useReactEffect(() => {
    const setHead = (manifest, appTitle, docTitle) => {
      const m = document.querySelector('link[rel="manifest"]');
      const t = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (m) m.setAttribute("href", manifest);
      if (t) t.setAttribute("content", appTitle);
      document.title = docTitle;
    };
    setHead("/manifest-admin.json", "Vault Admin", "The Family Vault · Admin");
    return () => setHead("/manifest.json", "The Family Vault", "The Family Vault");
  }, []);
  if (user === null) return <Loader />;
  if (user && user.is_super_admin)
    return <div style={{ background: "#0B0D14", minHeight: "100vh" }}><SuperAdmin /></div>;
  if (user) return <Navigate to="/" replace />;
  return <PlatformLogin />;
}

// Family invite link: /join/:code
function InviteRoute() {
  const { code } = useParams();
  const { user } = useAuth();
  const upper = (code || "").toUpperCase();

  useReactEffect(() => {
    if (upper) localStorage.setItem("fv_invite", upper);
  }, [upper]);

  if (user === null) return <Loader />;
  if (user && user.is_super_admin) return <Navigate to="/x1" replace />;
  if (user) return <Navigate to="/" replace />;
  return <FamilyLanding inviteCode={upper} />;
}

// Promotional referral link (from the jewel/lightning invite): /r/:code
function PromoRoute() {
  const { code } = useParams();
  const { user } = useAuth();
  const upper = (code || "").toUpperCase();
  if (user === null) return <Loader />;
  if (user && user.is_super_admin) return <Navigate to="/x1" replace />;
  if (user) return <Navigate to="/" replace />;
  return <PromoLanding code={upper} />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ActivityTracker />
          <Toaster
            position="top-center"
            theme="dark"
            toastOptions={{
              style: { background: "#222333", border: "1px solid rgba(124,131,255,0.25)", color: "#F2EBE1" },
            }}
          />
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/x1" element={<SuperAdminRoute />} />
            <Route path="/join/:code" element={<InviteRoute />} />
            <Route path="/r/:code" element={<PromoRoute />} />
            <Route path="/*" element={<MainRoute />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
